// attachments.mjs — 工单附件"挑选 → 上传"的纯逻辑（SB-042 约定：可判定的部分入 .mjs 配单测）。
//
// 病灶一（AST-000008 事故）：工单建好之后，前端**根本没有**补传附件的入口——全站唯一的
// <input type="file"> 只在提交表单里。美术看到「重新提交」旁边没有上传控件，就按最自然的
// 推断行事：撤回工单、重新交一张。结果 AST-000008 空附件作废、AST-000009 重开并补 7 个文件
// （34 MiB），工单号烧掉一个，请补信息的对话线索断掉。而后端（routes_me.cpp 的上传路由）**一直**
// 允许 pending / info_requested 两态上传——是界面比接口窄，不是规则不允许。canAttach() 就是把那条
// 后端判据一字不差地搬到前端，两边同一条件，界面不再比接口窄。第三百六十二批：那条判据如今是
// routes_me.cpp 的 attachment_window_open（上传与改说明共用），多了「ASK 提出方在 assigned 时」一格。
//
// 病灶二（丢照片）：原提交流程在上传出现部分失败之后**无条件清空**表单（setFiles([]) 紧跟
// setSuccess），失败的那张照片当场消失、无从重试——用户手上除了"再交一张工单"没有别的路。
// 所以本模块的上传返回的是**逐个文件的结局**，调用侧据此只清成功的、留下失败的（keepUnsent）。

// 每张工单的附件数量上限（与后端 defaults.h::kAttachmentsPerTicket 对齐）。
// 注意后端是**按整张工单已有的行数**封顶（ticket_store.cpp add_attachment 先 COUNT(*)），
// 不是按这一次挑了几个——所以补传面必须用 remainingQuota() 折算剩余额度，见下。
export const MAX_ATTACHMENTS = 10;

// ── 允许名单（唯一真源，界面侧）────────────────────────────────────────────────
// 一张表同时喂三样东西：挑选器的 accept 串、缩略图/角标的种类判定、以及**给人看的话术**。
// 病灶（第五十七批 D3）：话术原本是手写的整句 i18n（"图片/音频/视频…"），后端加了
// txt/md/csv/pdf 与 heic/heif 之后没人去改那句话，于是界面在告诉提交方"不能传 PDF"——
// 接口收、界面说不收，比界面比接口窄更坏（用户根本不会去试）。后端为同一个教训已经把
// 400 的话术改成由表生成（route_util.cpp attachment_allowlist_prose()，注释写着
// "由表生成,而不是手写 —— 400 的话术与真实允许集漂移过一次"），这里镜像同一做法。
// 任何一侧改名单，两侧的表都要改；attachments.test.mjs 用这张表逐格比对话术。
// 病灶三（AST-000011 / 第五十八批 C2）：图片档原本是 10MB。DS-A4a 要求固定测试集用
// **真实手持实拍**的饮食照片，而落地的那九张已经是 3.1–5.6 MiB —— 我们自己的闸拦住的
// 恰恰是验收标准点名要的那一类产物。提到 25MB（与文档档、与 defaults.h 同值）。
// 这张表是 defaults.h 的镜像；attachments.test.mjs 会真的去读 defaults.h 逐族对账，
// 因为"两处各写一个数字"正是这次事故的一半（另一半是话术手抄，见 D3）。
export const ATTACH_KINDS = [
  { kind: 'image', maxMB: 25, exts: ['png', 'jpg', 'jpeg', 'webp', 'gif', 'heic', 'heif'] },
  { kind: 'doc', maxMB: 25, exts: ['txt', 'md', 'csv', 'pdf'] },
  { kind: 'audio', maxMB: 50, exts: ['mp3', 'm4a', 'wav', 'ogg'] },
  { kind: 'video', maxMB: 200, exts: ['mp4', 'webm', 'mov'] },
  // package（第二百四十一批）：安卓安装包。承接方交付的签名 APK 此前只能挂网盘链接——那是一个
  // 对方随时能撤能换的指针，不是落在我方工单侧的交付物。安装形态只收 apk（aab 不经 bundletool 装不上），
  // 与视频档同值。它是可执行体：审核纪律见 SOP-09 §4——只核 sha256、只装测试机，工位上永不打开。
  // 此处此前还写着「zip 什么都能装故不收」——2026-09-19 裁定翻转了那句，但 zip 另成 archive 一族
  // （见下），**没有**并进本族：200MB 是为签名 APK 这一个交付物开的，容器不该蹭这个档。
  { kind: 'package', maxMB: 200, exts: ['apk'] },
  // animation / model（需求方 2026-09-17 裁定）：.riv 是 Rive 运行时文件——美术—程序资产对账表上的
  // 占位/成品动画就是它，此前进不了工单附件、只能挂外链（与 apk 当年同一种病）。.glb/.gltf 是
  // glTF 2.0 的两种载体，**为将来的 3D 预留**（规格书今天没有 3D 条目），与 riv 分族：
  // 后端 package 族被钉死为 ['apk']，这两族也各自钉住，免得"顺手加一个扩展名"把族义改了。
  // 三者都按音频档封顶（50MB，defaults.h 里有 static_assert 钉住"与音频同值"）。后端另有形状闸
  // （riv 首四字节 "RIVE"、glb 首四字节 "glTF"、gltf 须是带 asset 键的 JSON 对象）——那是字节判据，
  // 前端挑选时只按扩展名与容量预校验，贴错扩展名的文件会在上传时被后端 400 说清楚。
  // 它们是素材不是可执行体：审核照 §2 下载审看，落库进 assets/，不走 SOP-09 §4 的包类纪律。
  { kind: 'animation', maxMB: 50, exts: ['riv'] },
  { kind: 'model', maxMB: 50, exts: ['glb', 'gltf'] },
  // archive（需求方 2026-09-19 裁定）：zip。收它是为了同步相对复杂的代码与工具；
  // 条件是提交方在工单正文里声明解压后的文件名与各文件用途（人读的条件，不是本层的闸）。
  { kind: 'archive', maxMB: 50, exts: ['zip'] },
];

const extsOf = (kind) => ATTACH_KINDS.find((k) => k.kind === kind)?.exts || [];

// 文件选择器的过滤串。注意：accept 只是**挑选器的过滤**，不是校验——真正的允许名单在后端
// （attachment_kind_for）。heic/heif 是 iPhone 默认相册格式，不列出来的话手机上很多照片
// 在选择框里是灰的，用户以为"不支持"。由表生成，不再手抄。
export const ATTACH_ACCEPT = ATTACH_KINDS.flatMap((k) => k.exts).map((e) => `.${e}`).join(',');

const IMAGE_EXT = new Set(extsOf('image'));
// 浏览器**确定画得出来**的才做缩略图。heic/heif 除 Safari 外普遍解不出，硬塞进 <img> 会渲染
// 成碎图标——比不给缩略图更像坏了，所以退回到扩展名角标。（这是 image 族的真子集，
// 不能由表推出来，只能单列。）
const PREVIEWABLE_EXT = new Set(['png', 'jpg', 'jpeg', 'webp', 'gif']);
const AUDIO_EXT = new Set(extsOf('audio'));
const VIDEO_EXT = new Set(extsOf('video'));

// ── 名单话术（由上表生成）──────────────────────────────────────────────────────
// 只有"族名"是翻译（图片/文档/…），扩展名与容量上限一律从表里取——手抄的那一份
// 就是 D3 的病根。i18n 侧只保留可组装的碎片：kind.* / kindSpec / kindSep / helper / label。
export const attachKindLabelKey = (kind) => `submit.attach.kind.${kind}`;

export function attachKindsText(t) {
  return ATTACH_KINDS.map((k) => t(attachKindLabelKey(k.kind))).join(' / ');
}

// 提交表单上"附件（图片 / 文档 / 音频 / 视频）"那一行。
export function attachLabelText(t) {
  return t('submit.attach.label', { kinds: attachKindsText(t) });
}

// "最多 10 个；图片 ≤25MB（png/jpg/…），文档 ≤25MB（txt/md/csv/pdf），…"
export function attachHelperText(t, max = MAX_ATTACHMENTS) {
  const groups = ATTACH_KINDS.map((k) =>
    t('submit.attach.kindSpec', {
      kind: t(attachKindLabelKey(k.kind)),
      mb: String(k.maxMB),
      exts: k.exts.join('/'),
    }),
  ).join(t('submit.attach.kindSep'));
  return t('submit.attach.helper', { n: String(max), groups });
}

export function extOf(name) {
  const s = String(name || '');
  const i = s.lastIndexOf('.');
  if (i < 0 || i === s.length - 1) return '';
  return s.slice(i + 1).toLowerCase();
}

export const isImageName = (name) => IMAGE_EXT.has(extOf(name));
export const canPreview = (name) => PREVIEWABLE_EXT.has(extOf(name));

// 非图片项的单色角标文字：种类比扩展名更好认，认不出种类就退回扩展名（大写）。
export function badgeText(name) {
  const e = extOf(name);
  if (AUDIO_EXT.has(e)) return '♪';
  if (VIDEO_EXT.has(e)) return '▶';
  return (e || '?').toUpperCase().slice(0, 4);
}

// 人眼可读的大小。手机相册的 IMG_4021.jpg 之间只靠文件名是分不出来的（病灶三），
// 尺寸 + 缩略图才让人确认"挑对了那张饮食照"。
export function fmtSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ── 后端判据的镜像 ──────────────────────────────────────────────────────────
// routes_me.cpp 的 attachment_window_open（上传与改附件说明共用的那一只谓词；为假 ⇒ 409）：
//   · 归档一票否决；
//   · pending / info_requested ⇒ 可写（四类单的当事人只有提交人；ASK 一生到不了这两态）；
//   · 其余只有一格：指派单（type 'assigned'）在 `assigned` 时、且看客是**提出方**——第三百五十九批
//     需求方 2026-10-13 裁（OQ-E127 Q1）。受指派人作答（responded）之后窗即关；受指派人从头到尾
//     不开窗（需求方只裁了提出方这一半）。
// 任何一侧改动都必须同时改这里，否则界面又会比接口窄（或宽）。第三百五十九批开窗时这里就晚了
// 一批：ASK 提出方在「我的工单」里看不到上传框，而接口收（DP-2，第五十六批那一条的同型复发）。
// attachments.test.mjs 现在把后端那只谓词的源码读回来逐子句对账，后端再改一格，这里先红。
// 「我是谁」未知时不开：Number(undefined) 是 NaN，而 Number(null) 是 0——两个 null 相等不能算
// 「我是提出方」，所以要求一个正整数 id。只给两个参数（旧调用形状）时 ASK 一律不开：宁窄勿宽，
// 宽了是一次 409，窄了只是看不到框。
export function canAttach(status, archived, { type, submitterId, me } = {}) {
  if (archived) return false;
  if (status === 'pending' || status === 'info_requested') return true;
  if (type !== 'assigned' || status !== 'assigned') return false;
  const issuer = Number(submitterId);
  return Number.isInteger(issuer) && issuer > 0 && issuer === Number(me);
}

// 这张工单**还能再传几个**。后端 ticket_store.cpp:737 是按已有行数封顶的：一张已经有 7 个
// 附件的工单再传 5 个，后 2 个会 400（而且是英文的后端原话）。补传面原来不传 max，
// 于是永远提供 10 个额度——挑满 10 个、传上去 3 个、剩下 7 个逐一报错，用户完全看不懂。
// existingCount 未知（详情还没取回来）时返回满额：这里不假装知道，真正的闸在后端。
export function remainingQuota(existingCount, max = MAX_ATTACHMENTS) {
  const n = Number(existingCount);
  if (!Number.isFinite(n) || n < 0) return max;
  return Math.max(0, max - Math.trunc(n));
}

// ── 挑选时的预校验（第五十八批 C1）──────────────────────────────────────────
// 病灶（AST-000011）：挑选时**只**查了数量。类型与容量要等后端 400 才知道——而这两条
// 判据的数据（上面的 ATTACH_KINDS）早就在浏览器里，只被拿去拼一句说明文字。
// 后果不是"多点一次重试"：需求方按 DS-A4a 交了 10 张手持实拍饮食照，其中「海滩冲积物」
// 超了当时的 10MB 图片档，只有 9 张落地；而正文那张 10 行的表写着"按附件顺序排列"，
// 于是从第 3 行起每一行都在给**错的照片**贴标签——那些标签正是验收指标的地面真值。
// 判定在挑选时做，判据与后端逐格一致（routes_me.cpp：kind 为空 ⇒ 400；
// size == 0 || size > cap ⇒ 400）。
const specForName = (name) => ATTACH_KINDS.find((k) => k.exts.includes(extOf(name))) || null;

// 该文件名所属族的容量上限（字节）；不在允许名单里返回 0。
export function maxBytesFor(name) {
  const spec = specForName(name);
  return spec ? spec.maxMB * 1024 * 1024 : 0;
}

// null = 可以上传；否则返回**机器可辨**的拒绝描述（渲染交给 rejectText，i18n 归 i18n）。
export function validateFile(file) {
  const name = file?.name || '';
  const size = Number(file?.size) || 0;
  // 类型：accept 属性只是文件对话框的过滤器，用户切到「所有文件」照样能选 .exe——
  // 所以类型必须**在这里**按同一张表再判一次，不能指望挑选器。
  const spec = specForName(name);
  if (!spec) return { code: 'type', name, size, ext: extOf(name) };
  if (size <= 0) return { code: 'empty', name, size };
  const limit = spec.maxMB * 1024 * 1024;
  if (size > limit) return { code: 'size', name, size, kind: spec.kind, mb: spec.maxMB, limit };
  return null;
}

// 拒绝话术。必须**指名道姓**：哪个文件、它多大、上限多少——"文件过大"这种话让人
// 在十张相册照片里根本认不出是哪一张（IMG_4021.jpg 之间只看名字分不出来）。
export function rejectText(t, rej) {
  if (!rej) return '';
  if (rej.code === 'size') {
    return t('submit.attach.reject.size', {
      name: rej.name,
      size: fmtSize(rej.size),
      kind: t(attachKindLabelKey(rej.kind)),
      mb: String(rej.mb),
    });
  }
  if (rej.code === 'empty') return t('submit.attach.reject.empty', { name: rej.name });
  return t('submit.attach.reject.type', { name: rej.name, ext: rej.ext ? `.${rej.ext}` : '—' });
}

// ── 挑选项 ──────────────────────────────────────────────────────────────────
// 一项 = { key, file, name, size, status, error, reject, note, result }
//   status: 'ready' 待上传 | 'done' 已上传 | 'failed' 上传失败（留在表单里等重试）
//         | 'rejected' 预校验没过（**留在列表里**给人看原因、由人移除，绝不静默丢弃）
//   note:   提交方为**这一个文件**写的说明（第五十九批 E1；见文件末尾的备注清单一节）
//   result: 上传成功后后端 201 的回执 { id, filename, kind, bytes, tsMs }；其余状态为 null
let seq = 0;

export function makeItem(file) {
  seq += 1;
  return {
    key: `att${seq}`,
    file,
    name: file?.name || '',
    size: Number(file?.size) || 0,
    status: 'ready',
    error: '',
    reject: null,
    note: '',
    result: null,
  };
}

const sameFile = (a, b) =>
  a.name === (b?.name || '') &&
  a.size === (Number(b?.size) || 0) &&
  a.file?.lastModified === b?.lastModified;

// 额度只由「会真的上传的那些」占用：被拒的项永远不会成为这张工单的附件，
// 让它顶掉一个名额等于因为一个错文件少收一张好照片。
const quotaUsed = (arr) => arr.filter((it) => it.status !== 'rejected').length;

// 追加挑选结果：去重（相册里连点两下同一张是常事）+ 预校验 + 封顶。
// 返回 { items, added, duplicates, overflow, rejected }——overflow / rejected > 0 时
// 调用侧要**明说**，静默截断或静默丢弃等于又丢一次文件。
// 注意被拒项**不受 max 限制**地进入列表：要求就是"看得见、可移除"，把它们挡在门外
// 就又回到了"用户不知道少了哪一张"的事故现场。（一次挑选的规模由用户自己决定。）
export function addFiles(items, picked, max = MAX_ATTACHMENTS) {
  const cur = Array.isArray(items) ? items.slice() : [];
  const list = Array.from(picked || []);
  let added = 0;
  let duplicates = 0;
  let overflow = 0;
  let rejected = 0;
  for (const f of list) {
    if (cur.some((it) => sameFile(it, f))) {
      duplicates += 1;
      continue;
    }
    const rej = validateFile(f);
    if (rej) {
      cur.push({ ...makeItem(f), status: 'rejected', reject: rej });
      rejected += 1;
      continue;
    }
    if (quotaUsed(cur) >= max) {
      overflow += 1;
      continue;
    }
    cur.push(makeItem(f));
    added += 1;
  }
  return { items: cur, added, duplicates, overflow, rejected };
}

export const removeAt = (items, key) => (items || []).filter((it) => it.key !== key);

// ── 上传 ────────────────────────────────────────────────────────────────────
// 谁该被这一趟上传带走。**必须显式选择**，不能"除了 done 都传"——
// 病灶（第五十七批 D1）：提交之后表单还留在原地，用户会接着为**下一张**工单挑照片
// （status:'ready'）。此时点"重试上传"，旧代码只跳过 done，于是这些新挑的文件被传到
// retryNo ——**上一张**工单——去了；随后 keepUnsent() 又把它们（已 done）从表单里清掉。
// 用户的下一张照片落到了别人的工单上，界面上没有任何一处说过这件事。这正是本批要根治
// 的丢文件病，只是换了个方向。所以两条路径各有各的判据，调用侧必须挑明。
// 判据是**白名单**，不是"除了 done 都传"：'rejected' 落地之后，反面判据会把预校验
// 刚拦下的文件原封不动送去后端换一个英文 400 —— 闸等于没装。
export const UNSENT = (it) => it?.status === 'ready' || it?.status === 'failed'; // 提交路径
export const FAILED_ONLY = (it) => it?.status === 'failed'; // 重试路径：**只**重传失败的

// 逐个上传（后端是逐个 multipart 端点），返回**每一项的结局**。没被 select 选中的原样带回，
// 状态不动——重试之后 ready 项仍然是 ready，留给下一次提交。
// uploadFn(file) 由调用侧注入（提交面打到新建工单号，详情面打到当前工单号），
// 所以这个函数不依赖 DOM 也不依赖 api 模块，可以直接单测。
//
// 第五十九批 E1：成功项要把**后端 201 的回执**（routes_me.cpp:335
// `{id, filename, kind, bytes, tsMs}`）原样挂在 it.result 上。这不是"顺手记一下"——
// 它是备注清单唯一合法的行数据来源，理由见下面 noteRows() 的长注释：回执里的
// filename 是**落库名**（attachment_sanitize_filename 会把空格换成 _、把超过 120 字节的
// 名字截断），跟浏览器里的 file.name 可以不一样；用 file.name 写清单，等于给工作空间
// 一个磁盘上找不到的键。
export async function uploadItems(items, uploadFn, select = UNSENT) {
  const out = [];
  for (const it of items || []) {
    if (!select(it)) {
      out.push(it);
      continue;
    }
    try {
      // SB-073（第六十二批）：备注与文件**同一趟**送出去。此前 uploadFn 只收 file，
      // 备注留在页面里等人复制——那是零采用的根因，不是用户不认真。
      const res = await uploadFn(it.file, it.note);
      out.push({ ...it, status: 'done', error: '', result: res || null });
    } catch (e) {
      // 后端的自由文本原样保留（第 43 批语言规则：机器可辨的 code 才走 i18n）。
      // 失败项**不带 result**——这一点是下面那条结构性保证的另一半。
      out.push({ ...it, status: 'failed', error: e?.message || e?.code || '', result: null });
    }
  }
  return out;
}

export const doneCount = (items) => (items || []).filter((it) => it.status === 'done').length;
export const failedItems = (items) => (items || []).filter((it) => it.status === 'failed');
// 还没送出去的（ready + failed）。界面上的数字必须由它算——"只数 failed"会让新挑的文件
// 在计数里隐身，用户以为表单是空的（D1 的一半）。
export const readyItems = (items) => (items || []).filter((it) => it.status === 'ready');
export const unsentItems = (items) => (items || []).filter((it) => it.status !== 'done');
// 预校验没过的（永远不会上传，等人看原因并移除/换文件）。
export const rejectedItems = (items) => (items || []).filter((it) => it.status === 'rejected');
// 这一趟**真的会被送出去**的。界面的"上传"按钮要按它禁用：列表里只剩被拒项时
// 点上传什么也不会发生，那种沉默比报错更难懂。
export const sendableItems = (items) => (items || []).filter((it) => UNSENT(it));

// 每一项「会成为这张工单的第几个附件」；被拒的项不会上传，所以不占序号（null）。
// 病灶（AST-000011）：提交方在正文写了一张"按附件顺序排列"的 10 行表，实际只落地 9 个，
// 从第 3 行起全部错位。列表上只有文件名、没有序号，错位在屏幕上是**看不见**的。
// base = 这张工单已有的附件数（补传面用），提交面为 0。
export function attachIndexes(items, base = 0) {
  const raw = Number(base);
  let n = Number.isFinite(raw) && raw > 0 ? Math.trunc(raw) : 0;
  return (items || []).map((it) => {
    if (it?.status === 'rejected') return null;
    n += 1;
    return n;
  });
}

// 上传之后表单里该留下什么：成功的清掉、失败的留下。
// 这一行就是病灶二的解药——原来是无条件 setFiles([])。
export const keepUnsent = (items) => (items || []).filter((it) => it.status !== 'done');

// ── 补传草稿（页面级，按工单号）──────────────────────────────────────────────
// 病灶（第五十七批 D2）：「我的提交」的补传面把"挑好还没传的文件"存在详情面板自己的
// useState 里，而那个面板恰恰活不长——① 上传部分成功后要换 key 重挂一次去刷新附件清单，
// ② 行折叠是 unmountOnExit，③ TicketDetailView 在 loading / error 分支直接 return，
// 连 footer 都不渲染。于是**正好在部分失败、失败项最需要留住的时候**，一次慢的或失败的
// 重取就把它们连界面带状态一起抹掉。解药是把状态挪到页面级（比重取、比折叠活得久），
// 下面这几个纯函数就是那张表的全部写法，可单测。
//   drafts: { [ticketNo]: { files: Item[], attachCount: number|null, noteRows: Row[] } }
// noteRows（第五十九批 E1）同理必须住在页面级：说明清单是在**上传成功之后**才算得出
// 来的，而"上传成功"正是那次换 key 重挂的触发条件——把它放在面板里，等于每次算出来
// 就立刻丢掉。
export const EMPTY_DRAFT = Object.freeze({ files: [], attachCount: null, noteRows: [] });

export const draftOf = (drafts, no) => (drafts && drafts[no]) || EMPTY_DRAFT;

export function putDraftFiles(drafts, no, files) {
  const map = drafts || {};
  const cur = draftOf(map, no);
  const next = files || [];
  if (cur.files === next) return map;
  return { ...map, [no]: { ...cur, files: next } };
}

// count 未知用 null 表示。**同值必须原样返回原引用**：这个 setter 由详情取数完成后的
// effect 调用，每次都换新对象会让 effect 自激成死循环（渲染→setState→渲染）。
export function putDraftCount(drafts, no, count) {
  const map = drafts || {};
  const cur = draftOf(map, no);
  const raw = Number(count);
  const next = count == null || !Number.isFinite(raw) ? null : Math.max(0, Math.trunc(raw));
  if (cur.attachCount === next) return map;
  return { ...map, [no]: { ...cur, attachCount: next } };
}

// 说明行按附件 id **归并**着攒（不是覆盖）：补传面分两次各传几个文件，第二次的
// 上传结局里没有第一次那些附件，直接覆盖就把先前的说明冲没了。
export function putDraftNoteRows(drafts, no, rows) {
  const map = drafts || {};
  const cur = draftOf(map, no);
  const merged = mergeNoteRows(cur.noteRows || [], rows || []);
  if (merged.length === (cur.noteRows || []).length && merged.every((r, i) => r === cur.noteRows[i])) {
    return map;                            // 同值返回原引用（与 putDraftCount 同理）
  }
  return { ...map, [no]: { ...cur, noteRows: merged } };
}

// ══ 逐附件备注 → 机器可读的说明清单（第五十九批 E1）══════════════════════════
//
// 病灶（AST-000011，本仓库记过两次的那一次）：提交方在正文手打了一张 10 行的
// markdown 表，表头写着「按附件顺序排列」，逐行给出每张饮食照的来源 / 类别 / 内容。
// 其中一张超了当时的图片档，只有 9 张落地——**表还是 10 行**，于是从第 3 行起每一行
// 都在给错的照片贴标签。而这些标签正是 DS-A6c 召回率/误报率的地面真值：错位的真值
// 会静默污染验收数字，全流程没有任何一处会报错。
//
// 这一节把「清单与附件一一对应」从**劝告**变成**构造上的性质**，靠两件事：
//
//   ① 行数据只能从**上传回执**里长出来。一行需要 id 与 filename，这两个字段只有
//      后端 201 才给得出（uploadItems 挂在 it.result 上）。挑选项身上根本没有它们，
//      失败项的 result 是 null——所以"拿挑选清单生成表格"不是被 if 过滤掉的，是
//      **无从写起**。想违反它得先伪造一份回执。
//   ② 行与文件的绑定不用位置，用 id + 落库文件名。位置耦合少一个元素就整体失准；
//      键控少一条只是少一条，而且下游 scripts/lint/testset-labels.mjs 的第 ① 条判据
//      （标注指向的文件必须存在）会**当场报错**，不会闷声算错。
//      filename 取回执里的落库名而不是浏览器里的 file.name：后端
//      attachment_sanitize_filename 会把空格/特殊字符换成 _、并把超过 120 字节的名字
//      截断，两者可以不一样；用 file.name 写清单就是给工作空间一个磁盘上不存在的键。
//      id 才是真正的主键——两个不同的原名（"a b.jpg" 与 "a-b.jpg"）会被净化成同一个
//      落库名，只按文件名归并会**悄悄吃掉一条**，正是本节要根治的那种病。
//
// 载体选 JSON 而不是 markdown 表格，理由写在 renderNotesBlock 上。

// 单条备注的长度上限。够写清「来源 / 类别 / 内容」，又不至于让正文被一条备注淹没。
export const NOTE_MAX = 500;

// 备注是挑选项的一部分（跟着 key 走，与位置无关）。移除一个文件，它的备注跟着消失。
export function setItemNote(items, key, note) {
  const s = String(note ?? '').slice(0, NOTE_MAX);
  return (items || []).map((it) => (it.key === key ? { ...it, note: s } : it));
}

export const noteText = (it) => String(it?.note ?? '').trim();

// 一个挑选项能否产出清单里的一行。**唯一的判据**，noteRows 与 orphanNoteItems 共用，
// 免得"能产出"与"算不算漏掉"两处各判一次、判歪一处就又开始静默丢东西。
function rowOf(it) {
  if (it?.status !== 'done') return null;          // 没上去的文件不许有行——这就是整条修复
  const r = it.result;
  if (!r || r.id == null || !r.filename) return null;  // 没有回执 ⇒ 不知道落库名 ⇒ 不编
  const note = noteText(it);
  if (!note) return null;                          // 没写备注就不占一行（空表不如没表）
  return {
    id: Number(r.id),
    filename: String(r.filename),
    bytes: Number(r.bytes) || 0,
    note,
  };
}

// 清单的行。顺序是上传顺序，但**顺序不承载语义**——每行自带 id 与文件名。
export function noteRows(items) {
  const out = [];
  for (const it of items || []) {
    const row = rowOf(it);
    if (row) out.push(row);
  }
  return out;
}

// 写了备注、**试过了**却产不出行的项（上传失败、被预校验拦下、或成功了但后端没给回执）。
// 调用侧必须把这个数说出来：静默吞掉一条备注，就是又一次"地面真值悄悄少一条"，
// 与错位是同一个病，只是少了一行而不是错了一行。
// 'ready' 不算——它只是还没轮到，此刻报"这条说明没进清单"是虚惊：补传面的挑选器里
// 常年躺着刚挑好还没点上传的文件，那句警告会一直挂在屏幕上，然后就没人再看它了。
export const orphanNoteItems = (items) =>
  (items || []).filter(
    (it) => noteText(it) && it.status !== 'ready' && !rowOf(it),
  );

// ── 清单块的载体 ────────────────────────────────────────────────────────────
// 为什么是 JSON 而不是 markdown 表格：
//   · 正文在详情页是 whiteSpace:'pre-wrap' 的**纯文本**，markdown 不会被渲染成表格。
//     一张 `| a | b | c |` 的管道表在 390px 上会逐行折行，折完之后列全串了——读起来
//     比没有还糟，而"读起来串了"正是我们要根治的那种错觉。JSON 每个附件占一行，
//     折行也只是这一行变两行，键名还跟在值旁边，串不了。
//   · 备注里出现一个 `|` 或一个换行，markdown 表格当场散架且不报错；JSON 里它们只是
//     被转义的字符。载体本身不能有"写错一个字符就静默错位"的性质。
//   · 下游已经有一个机器消费者：scripts/lint/testset-labels.mjs 读的是**按文件名键控**
//     的 jsonc 标注表 { labels: { "<文件名>": {source,category,content} } }。本块是
//     JSON，工作空间把它转成那张表只是一次 map，不必写解析器；而且解析失败会抛错，
//     不会像正则抠表格那样"抠出半张表"继续往下算。
//   · 人也读得懂：一个附件一行，id / 文件名 / 字节数 / 备注四个键名自解释。
//
// 用带标签的围栏包起来（```json attachment-notes …）：这既是给人看的"这块是机器生成的"
// 标记，也是幂等重写时唯一的定位锚（见 withNotesBlock）。about 一句人话放在块**内部**，
// 这样整块只有一个起点一个终点，剥离时不会漏掉标题行。
export const NOTES_FENCE_TAG = 'attachment-notes';
const FENCE_OPEN = `\`\`\`json ${NOTES_FENCE_TAG}`;
// 起于带标签的围栏、止于独占一行的 ```；非贪婪，正文里再出现别的围栏也不会被吞进来。
const BLOCK_RE = new RegExp(
  String.raw`(?:\r?\n)*^\`\`\`json ${NOTES_FENCE_TAG}\r?\n([\s\S]*?)\r?\n\`\`\`[ \t]*$`,
  'm',
);

export function renderNotesBlock(rows, about = '') {
  const list = rows || [];
  if (list.length === 0) return '';       // 空清单不产出块（要求 5：绝不贴一个空表头）
  // 一个附件一行：JSON.stringify(row) 单行 + 手工缩进。默认的 indent:2 会把每行摊成
  // 五行，十张照片就是五十行，正文里再也找不到别的内容；而单行版在 390px 上折行也
  // 只是这一行变两行，键名仍跟在值旁边。
  const items = list.map((r) => `    ${JSON.stringify(r)}`).join(',\n');
  const head = about ? `  ${JSON.stringify('about')}: ${JSON.stringify(about)},\n` : '';
  return `${FENCE_OPEN}\n{\n${head}  "attachmentNotes": [\n${items}\n  ]\n}\n\`\`\``;
}

// 正文里已有的块 → { rows, index, length }；没有或解析不了返回 null。
// 解析不了**不当作空**：那会让"我看不懂你的块"变成"我把你的块删了"，比留着更坏。
export function parseNotesBlock(text) {
  const s = String(text ?? '');
  const m = BLOCK_RE.exec(s);
  if (!m) return null;
  let rows = [];
  try {
    const j = JSON.parse(m[1]);
    const arr = Array.isArray(j?.attachmentNotes) ? j.attachmentNotes : [];
    rows = arr
      .filter((r) => r && r.id != null && r.filename)
      .map((r) => ({
        id: Number(r.id),
        filename: String(r.filename),
        bytes: Number(r.bytes) || 0,
        note: String(r.note ?? ''),
      }));
  } catch {
    return null;                          // 坏块：交给 withNotesBlock 原样保留
  }
  return { rows, index: m.index, length: m[0].length, raw: m[0] };
}

// 按 id 归并：id 是后端主键，附件 id 相同就是同一个附件（重传/改备注），新的覆盖旧的
// 并**留在原位**；id 没见过的追加到末尾。文件名不能当归并键——净化之后两个不同的原名
// 可以撞成同一个落库名，按名归并会悄悄吃掉一条。
export function mergeNoteRows(existing, incoming) {
  const out = (existing || []).slice();
  const at = new Map(out.map((r, i) => [r.id, i]));
  for (const r of incoming || []) {
    if (at.has(r.id)) out[at.get(r.id)] = r;
    else {
      at.set(r.id, out.length);
      out.push(r);
    }
  }
  return out;
}

// 幂等地把清单块写进一段文本（正文 / 回复正文）。
// 要求 6（重复提交不得污染）：**先剥离已有的块、按 id 归并、再整块重写**，而不是往
// 后面再贴一块。贴第二块的话，正文里就有两份互相矛盾的地面真值，谁也说不清哪份算数
// ——比错位更难查。归并保留旧行：补传面第二次只传了 1 个文件，前 9 条说明不能因此蒸发。
// 没有新行、也没有旧块时原样返回（要求 5）。
export function withNotesBlock(text, rows, about = '') {
  const s = String(text ?? '');
  const prev = parseNotesBlock(s);
  // 坏块（解析不了）：不动它，把新块贴在后面。宁可两块并存让人看见，也不静默删掉
  // 用户/别的工具写下的东西。
  if (BLOCK_RE.test(s) && prev === null) {
    const add = renderNotesBlock(rows, about);
    return add ? `${s.replace(/\s+$/, '')}\n\n${add}` : s;
  }
  const merged = mergeNoteRows(prev?.rows || [], rows || []);
  const base = (prev ? s.slice(0, prev.index) + s.slice(prev.index + prev.length) : s)
    .replace(/\s+$/, '');
  const block = renderNotesBlock(merged, about);
  if (!block) return base;
  return base ? `${base}\n\n${block}` : block;
}
